import React, { memo, useCallback } from 'react';
import { PhaseData } from '../../hooks/useSystemData';

interface PhaseDetailsCardProps {
  voltage?: PhaseData;
  current?: PhaseData;
  power?: PhaseData & { total?: number };
  reactivePower?: PhaseData;
}

export const PhaseDetailsCard = memo(function PhaseDetailsCard({ voltage, current, power, reactivePower }: PhaseDetailsCardProps) {
  const phases = [
    { name: 'L1', color: 'bg-red-500', key: 'phase1' as const },
    { name: 'L2', color: 'bg-yellow-500', key: 'phase2' as const },
    { name: 'L3', color: 'bg-blue-500', key: 'phase3' as const },
  ];

  const formatValue = (value: number | undefined, decimals = 1) => {
    if (value === undefined || value === null) return '-';
    return value.toFixed(decimals);
  };

  const getPowerColor = (watts: number | undefined) => {
    if (!watts) return 'text-slate-400';
    if (watts > 0) return 'text-green-400';
    if (watts < 0) return 'text-red-400';
    return 'text-slate-400';
  };

  return (
    <div className="dashboard-card p-4 sm:p-6">
      <h3 className="text-xs sm:text-sm font-medium text-slate-400 uppercase tracking-wide mb-3 sm:mb-4">
        Phase Details
      </h3>

      <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
        <table className="w-full min-w-[320px]">
          <thead>
            <tr className="text-left text-xs text-slate-500 uppercase">
              <th className="pb-2 sm:pb-3 font-medium">Phase</th>
              <th className="pb-2 sm:pb-3 font-medium text-right">Voltage</th>
              <th className="pb-2 sm:pb-3 font-medium text-right">Current</th>
              <th className="pb-2 sm:pb-3 font-medium text-right">Power</th>
              <th className="pb-2 sm:pb-3 font-medium text-right hidden sm:table-cell">Reactive</th>
            </tr>
          </thead>
          <tbody className="text-xs sm:text-sm">
            {phases.map((phase) => (
              <tr key={phase.name} className="border-t border-dashboard-border">
                <td className="py-2 sm:py-3">
                  <div className="flex items-center space-x-1.5 sm:space-x-2">
                    <div className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full ${phase.color}`}></div>
                    <span className="text-white font-medium">{phase.name}</span>
                  </div>
                </td>
                <td className="py-2 sm:py-3 text-right text-white">
                  {formatValue(voltage?.[phase.key])} V
                </td>
                <td className="py-2 sm:py-3 text-right text-white">
                  {formatValue(current?.[phase.key])} A
                </td>
                <td className={`py-2 sm:py-3 text-right font-medium ${getPowerColor(power?.[phase.key])}`}>
                  {formatValue(power?.[phase.key], 0)} W
                </td>
                <td className="py-2 sm:py-3 text-right text-slate-400 hidden sm:table-cell">
                  {formatValue(reactivePower?.[phase.key], 0)} VAR
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-dashboard-border">
              <td className="py-2 sm:py-3">
                <span className="text-slate-400 font-medium">Total</span>
              </td>
              <td className="py-2 sm:py-3 text-right text-slate-400">-</td>
              <td className="py-2 sm:py-3 text-right text-slate-400">-</td>
              <td className={`py-2 sm:py-3 text-right font-bold ${getPowerColor(power?.total)}`}>
                {formatValue(power?.total, 0)} W
              </td>
              <td className="py-2 sm:py-3 text-right text-slate-400 hidden sm:table-cell">-</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
});

export default PhaseDetailsCard;
